import { formatTime12Hour } from "@/utils/timeFormatUtils";
import { Eyebrow } from "@/components/brand/SectionHeader";

interface EventInformationProps {
  event: {
    time: string;
    location: string;
    city: string;
    nameOfLocation: string;
    specialInstructions?: string;
    deliveryInfo?: string;
    dealerSpecialInstructions?: string;
    extraPayPerDealer?: number;
    setupTime?: string;
    parkingInfo?: string;
  };
}

/**
 * The event's details, as the approved preview sets them: a small muted label
 * with its value beneath, in two columns.
 *
 * It used to be a gold icon beside an 18px semibold heading, per field. That
 * gave a parking note the same typographic weight as the event's own title
 * and, with six of them stacked, made a reference grid look like six separate
 * announcements. A field label is not a section heading — see the two roles in
 * SectionHeader — and these are field labels.
 *
 * The tinted background is gone too. The strip above is tinted to mark the one
 * cell that matters; a second tinted band directly under it spent that signal
 * on a block of addresses.
 */
export default function EventInformation({ event }: EventInformationProps) {
  return (
    <div className="grid gap-x-6 gap-y-5 px-6 py-6 sm:grid-cols-2">
      {event.setupTime && (
        <Field label="Setup Time">{formatTime12Hour(event.setupTime)}</Field>
      )}

      <Field label="Location">
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            `${event.location}, ${event.city}`,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-1 underline-offset-4 transition-colors hover:text-primary hover:decoration-2"
        >
          {event.location}
        </a>
        <span className="block text-muted-foreground">{event.city}</span>
      </Field>

      <Field label="Venue">{event.nameOfLocation}</Field>

      {event.extraPayPerDealer > 0 && (
        <Field label="Bonus Pay">
          Additional ${event.extraPayPerDealer} per dealer
        </Field>
      )}

      {event.specialInstructions && (
        <Field label="Instructions">{event.specialInstructions}</Field>
      )}

      {event.deliveryInfo && (
        <Field label="Delivery Notes">{event.deliveryInfo}</Field>
      )}

      {event.parkingInfo && (
        <Field label="Parking Information">{event.parkingInfo}</Field>
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Eyebrow>{label}</Eyebrow>
      <p className="mt-1 text-foreground">{children}</p>
    </div>
  );
}
